import { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useScroll, useSpring, MotionValue } from "motion/react";
import * as THREE from "three";

// ─── GLSL Sources ────────────────────────────────────────────────────────────

const CONSTANTS_GLSL = `
#define M_PI    3.14159265358979323846264338327950
#define M_2PI   6.28318530717958647692528676655900
#define M_PI2   1.57079632679489661923132169163975
#define EPS     0.0001
#define EQUALS(A,B) ( abs((A)-(B)) < EPS )
#define EQUALSZERO(A) ( ((A)<EPS) && ((A)>-EPS) )
`;

const RAND_GLSL = `
float rand(vec2 seed) {
    return fract(sin(dot(seed.xy,vec2(12.9898,78.233))) * 43758.5453);
}
`;

const NOISE_GLSL = `
vec4 _mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec3 _mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec2 _mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
float _mod289(float x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 _permute(vec4 x){return _mod289(((x*34.0)+1.0)*x);}
vec3 _permute(vec3 x){return _mod289(((x*34.0)+1.0)*x);}
float _permute(float x){return _mod289(((x*34.0)+1.0)*x);}
vec4 _taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float _taylorInvSqrt(float r){return 1.79284291400159-0.85373472095314*r;}

float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);
  const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));
  vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);
  vec3 l=1.0-g;
  vec3 i1=min(g.xyz,l.zxy);
  vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;
  vec3 x2=x0-i2+C.yyy;
  vec3 x3=x0-D.yyy;
  i=_mod289(i);
  vec4 p=_permute(_permute(_permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;
  vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);
  vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy;
  vec4 y=y_*ns.x+ns.yyyy;
  vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);
  vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0;
  vec4 s1=floor(b1)*2.0+1.0;
  vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
  vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);
  vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);
  vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=_taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
  m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}

vec3 snoiseVec3(vec3 x){
  return vec3(
    snoise(x),
    snoise(vec3(x.y-19.1,x.z+33.4,x.x+47.2)),
    snoise(vec3(x.z+74.2,x.x-124.5,x.y+99.4))
  );
}

vec3 curlNoise(vec3 p){
  const float e=1e-1;
  vec3 dx=vec3(e,0,0),dy=vec3(0,e,0),dz=vec3(0,0,e);
  vec3 p_x0=snoiseVec3(p-dx),p_x1=snoiseVec3(p+dx);
  vec3 p_y0=snoiseVec3(p-dy),p_y1=snoiseVec3(p+dy);
  vec3 p_z0=snoiseVec3(p-dz),p_z1=snoiseVec3(p+dz);
  float x=p_y1.z-p_y0.z-p_z1.y+p_z0.y;
  float y=p_z1.x-p_z0.x-p_x1.z+p_x0.z;
  float z=p_x1.y-p_x0.y-p_y1.x+p_y0.x;
  return normalize(vec3(x,y,z)/(2.0*e));
}
`;

const SIM_INIT_FRAG = `
precision highp float;
${CONSTANTS_GLSL}
${RAND_GLSL}
varying vec2 vUv;
void main(){
  vec2 coords=vUv;
  coords.x=coords.x*M_2PI-M_PI;
  coords.y=mod(coords.y+0.5,1.0)*M_PI;
  vec3 sc=vec3(sin(coords.y)*cos(coords.x),cos(coords.y),sin(coords.y)*sin(coords.x));
  vec3 pos=sc*rand(vUv)*5.0;
  gl_FragColor=vec4(pos,1.0);
}
`;

const SIM_VERT = `
varying vec2 vUv;
void main(){
  vUv=uv;
  gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);
}
`;

const SIM_FRAG = `
precision highp float;
${CONSTANTS_GLSL}
${RAND_GLSL}
${NOISE_GLSL}

varying vec2 vUv;
uniform sampler2D tPrev;
uniform sampler2D tCurr;
uniform float uDeltaT;
uniform float uTime;
uniform float uScrollShape;
uniform float uOffsetX;
uniform vec3  uMouseLocal;
uniform float uHoverStrength;

void main(){
  vec3 prevPos = texture2D(tPrev, vUv).rgb;
  vec3 currPos = texture2D(tCurr, vUv).rgb;
  vec3 vel = (currPos - prevPos) / max(uDeltaT, 0.0001);

  float K_VEL_DECAY = 0.98;
  vec3 accel = vec3(0.0);

  // ---- shape 0: galaxy ----
  {
    float radius=vUv.y;
    float theta=vUv.x*M_2PI;
    float rv=rand(vec2(theta,radius));
    radius+=rv*0.5; theta+=rv*0.5;
    float spin=theta+radius*radius-0.25*uTime;
    vec3 gTarget=vec3(radius*sin(spin),0.3*(rv-0.5)*cos(0.6*radius*M_PI/2.0)*cos(0.6*radius*M_PI/2.0),radius*cos(spin))*3.0;
    vec3 toG=gTarget-currPos;
    float lenG=length(toG);
    vec3 accelGalaxy=3.0*toG/max(lenG,EPS)*rv;
    accelGalaxy+=0.1*curlNoise(currPos);

    // ---- shape 1: sphere ----
    vec2 sc2=vUv;
    sc2.x=sc2.x*M_2PI-M_PI;
    sc2.y=sc2.y*M_PI;
    vec3 sSphere=vec3(sin(sc2.y)*cos(sc2.x),cos(sc2.y),sin(sc2.y)*sin(sc2.x))*2.0;
    vec3 toSph=sSphere-currPos;
    float lenSph=length(toSph);
    vec3 accelSphere=13.0*toSph/max(lenSph,EPS);

    // ---- shape 2: rose galaxy ----
    float radius2=vUv.y*M_PI;
    float theta2=vUv.x*M_2PI;
    vec3 rTarget=vec3(radius2*sin(theta2),radius2*radius2*sin(4.0*theta2+sin(3.0*M_PI*radius2+uTime/2.0))/10.0,radius2*cos(theta2));
    vec3 toR=rTarget-currPos;
    float lenR=length(toR);
    vec3 accelRose=12.0*toR/max(lenR,EPS);

    // ---- shape 3: torus ----
    float tR = 1.6; float tP = 0.6;
    float thetaT = rand(vUv * 1.5) * M_2PI;
    float phiT   = rand(vUv * 2.5) * M_2PI;
    vec3 sTorus = vec3((tR+tP*cos(phiT))*cos(thetaT), tP*sin(phiT), (tR+tP*cos(phiT))*sin(thetaT));
    vec3 toTorus = sTorus - currPos;
    float lenTorus = length(toTorus);
    vec3 accelTorus=13.0*toTorus/max(lenTorus,EPS);

    // ---- shape 4: cube ----
    float side = floor(rand(vUv * 7.7) * 6.0);
    float uC = rand(vUv * 3.3) * 2.0 - 1.0;
    float vC = rand(vUv * 9.1) * 2.0 - 1.0;
    vec3 sCube;
    if (side == 0.0)      sCube = vec3(uC, vC,  1.0);
    else if (side == 1.0) sCube = vec3(uC, vC, -1.0);
    else if (side == 2.0) sCube = vec3(uC,  1.0, vC);
    else if (side == 3.0) sCube = vec3(uC, -1.0, vC);
    else if (side == 4.0) sCube = vec3( 1.0, uC, vC);
    else                  sCube = vec3(-1.0, uC, vC);
    sCube *= 1.8;
    vec3 toCube = sCube - currPos;
    float lenCube = length(toCube);
    vec3 accelCube=13.0*toCube/max(lenCube,EPS);

    // ---- shape 5: circuit grid ----
    float gridRange = 4.0;
    vec3 sGrid = vec3(
      (floor(rand(vUv)       * 8.0) / 8.0 - 0.5) * gridRange,
      (floor(rand(vUv + 0.1) * 8.0) / 8.0 - 0.5) * gridRange,
      (floor(rand(vUv + 0.2) * 3.0) / 3.0 - 0.5) * 0.6
    );
    float traceCheck = rand(vUv + 0.3);
    if      (traceCheck > 0.8) sGrid.x += (rand(vUv + 0.4) - 0.5) * 0.4;
    else if (traceCheck > 0.6) sGrid.y += (rand(vUv + 0.5) - 0.5) * 0.4;
    vec3 toGrid = sGrid - currPos;
    float lenGrid = length(toGrid);
    vec3 accelGrid=13.0*toGrid/max(lenGrid,EPS);

    // ---- blend shapes ----
    float s = clamp(uScrollShape, 0.0, 1.0) * 5.0;
    if (s <= 0.0) {
      accel = accelGalaxy;
    } else if (s >= 5.0) {
      accel = accelGrid;
    } else {
      float idx  = floor(s);
      float frac = fract(s);
      vec3 a0, a1;
      if      (idx < 1.0) { a0 = accelGalaxy; a1 = accelSphere; }
      else if (idx < 2.0) { a0 = accelSphere; a1 = accelRose;   }
      else if (idx < 3.0) { a0 = accelRose;   a1 = accelTorus;  }
      else if (idx < 4.0) { a0 = accelTorus;  a1 = accelCube;   }
      else                { a0 = accelCube;   a1 = accelGrid;   }
      accel = mix(a0, a1, frac);
    }
  }

  accel += vec3(uOffsetX * 0.6 - currPos.x, 0.0, 0.0) * 0.15;

  if (uHoverStrength > 0.001) {
    vec3  diff   = currPos - uMouseLocal;
    float dist   = length(diff);
    float radius = 1.8;

    if (dist < radius && dist > EPS) {
      float t        = 1.0 - (dist / radius);
      float strength = t * t * t;
      accel += normalize(diff) * strength * uHoverStrength * 22.0;
    }
  }

  vel     = K_VEL_DECAY * vel + accel * uDeltaT;
  currPos = currPos + vel * uDeltaT;
  gl_FragColor = vec4(currPos, 1.0);
}
`;

const PARTICLE_VERT = `
precision highp float;
uniform sampler2D tPos;
uniform float uTime;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uHighlightColor;
uniform float uColorFreq;
uniform float uColorSpeed;
uniform float uPointSize;
varying vec3 vColor;

float rand(vec2 co){
    return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
}

void main(){
  vColor=uColor1;
  if (rand(position.xy) > 0.95) {
    vColor = uHighlightColor;
  }
  vec4 posSample=texture2D(tPos,position.xy);
  vec3 pos=posSample.rgb;
  vec3 camToPos=pos-cameraPosition;
  float camDist=length(camToPos);
  gl_PointSize=max(uPointSize*6.0/camDist, 0.5);
  gl_Position=projectionMatrix*modelViewMatrix*vec4(pos,1.0);
}
`;

const PARTICLE_FRAG = `
precision highp float;
varying vec3 vColor;
uniform float uAlpha;
void main(){
  float dist = distance(gl_PointCoord, vec2(0.5));
  if (dist > 0.5) discard;
  float alpha = smoothstep(0.5, 0.4, dist);
  gl_FragColor=vec4(vColor,uAlpha*alpha);
}
`;

// ─── Light-theme palette ──────────────────────────────────────────────────────
// Rich, saturated deep tones that read clearly on white/light backgrounds.
// Each value is a soft pastel — light, airy tones that read as faint dust on light backgrounds.
//
// Light-mode palette — soft pastel tones for a delicate dust-mote feel.
//
// Highlight (the ~5 % sparkle particles) is a soft pastel accent — light and airy.
// pops as an accent rather than washing out: near-black with a slight hue.
const LIGHT_PALETTE = [
  new THREE.Vector3(0.6, 0.55, 0.95), // 0 – Light Lavender
  new THREE.Vector3(0.4, 0.65, 0.95), // 1 – Light Blue
  new THREE.Vector3(0.7, 0.3, 0.75), // 2 – Muted Violet
  new THREE.Vector3(0.9, 0.45, 0.55), // 3 – Soft Rose
  new THREE.Vector3(0.3, 0.75, 0.65), // 4 – Muted Teal
  new THREE.Vector3(0.55, 0.45, 0.85), // 5 – Soft Indigo
];

// ─── Dark-theme palette (unchanged) ──────────────────────────────────────────
const DARK_PALETTE = [
  new THREE.Vector3(0.2, 0.3, 0.95), // 0 – Indigo
  new THREE.Vector3(0.0, 0.5, 0.9), // 1 – Azure
  new THREE.Vector3(0.4, 0.1, 0.9), // 2 – Purple
  new THREE.Vector3(0.8, 0.2, 0.4), // 3 – Rose
  new THREE.Vector3(0.1, 0.8, 0.5), // 4 – Emerald
  new THREE.Vector3(0.3, 0.3, 0.95), // 5 – Indigo
];

function createSimMesh(material: THREE.ShaderMaterial) {
  const geo = new THREE.PlaneGeometry(2, 2);
  return new THREE.Mesh(geo, material);
}

const SIM_SIZE = 354;

function createFloatTarget(size: number) {
  return new THREE.WebGLRenderTarget(size, size, {
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    format: THREE.RGBAFormat,
    type: THREE.FloatType,
    depthBuffer: false,
    stencilBuffer: false,
  });
}

interface ParticleSystemProps {
  isDark: boolean;
  scrollProgress: MotionValue<number>;
  ndcMouse: React.RefObject<THREE.Vector2>;
}

function ParticleSystem({
  isDark,
  scrollProgress,
  ndcMouse,
}: ParticleSystemProps) {
  const { gl } = useThree();

  const targets = useRef<THREE.WebGLRenderTarget[]>([]);
  const targetIdx = useRef(0);
  const hoverStrength = useRef(0);

  const initMat = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`,
        fragmentShader: SIM_INIT_FRAG,
      }),
    [],
  );

  const simMat = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: SIM_VERT,
        fragmentShader: SIM_FRAG,
        uniforms: {
          tPrev: { value: null },
          tCurr: { value: null },
          uDeltaT: { value: 0.016 },
          uTime: { value: 0 },
          uScrollShape: { value: 0 },
          uOffsetX: { value: 0 },
          uMouseLocal: { value: new THREE.Vector3() },
          uHoverStrength: { value: 0 },
        },
      }),
    [],
  );

  const drawMat = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: PARTICLE_VERT,
        fragmentShader: PARTICLE_FRAG,
        uniforms: {
          tPos: { value: null },
          uTime: { value: 0 },
          uColor1: { value: new THREE.Vector3(0.6, 0.6, 1.0) },
          uColor2: { value: new THREE.Vector3(0.4, 0.2, 0.9) },
          uHighlightColor: { value: new THREE.Vector3(1.0, 1.0, 1.0) },
          uColorFreq: { value: 1.5 },
          uColorSpeed: { value: 0.5 },
          uPointSize: { value: 2.5 },
          uAlpha: { value: 0.5 },
        },
        transparent: true,
        depthWrite: false,
        // Use NormalBlending for light mode (dark particles blend correctly).
        // AdditiveBlending makes dark colors invisible on light backgrounds.
        blending: THREE.NormalBlending,
      }),
    [],
  );

  const particleGeo = useMemo(() => {
    const total = SIM_SIZE * SIM_SIZE;
    const positions = new Float32Array(total * 3);
    for (let i = 0; i < SIM_SIZE; i++)
      for (let j = 0; j < SIM_SIZE; j++) {
        const idx = (i * SIM_SIZE + j) * 3;
        positions[idx] = (j + 0.5) / SIM_SIZE;
        positions[idx + 1] = (i + 0.5) / SIM_SIZE;
        positions[idx + 2] = 0;
      }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    return geo;
  }, []);

  const simCamera = useMemo(
    () => new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1),
    [],
  );
  const simScene = useMemo(() => new THREE.Scene(), []);
  const simMesh = useMemo(() => createSimMesh(simMat), [simMat]);
  const initScene = useMemo(() => new THREE.Scene(), []);
  const initMesh = useMemo(() => createSimMesh(initMat), [initMat]);

  useEffect(() => {
    simScene.add(simMesh);
    initScene.add(initMesh);
  }, [simScene, simMesh, initScene, initMesh]);

  useEffect(() => {
    const t0 = createFloatTarget(SIM_SIZE);
    const t1 = createFloatTarget(SIM_SIZE);
    const t2 = createFloatTarget(SIM_SIZE);
    targets.current = [t0, t1, t2];

    const prev = gl.getRenderTarget();
    gl.setRenderTarget(t0);
    gl.render(initScene, simCamera);
    gl.setRenderTarget(t1);
    gl.render(initScene, simCamera);
    gl.setRenderTarget(t2);
    gl.render(initScene, simCamera);
    gl.setRenderTarget(prev);

    drawMat.uniforms.tPos.value = t0.texture;
    return () => {
      t0.dispose();
      t1.dispose();
      t2.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl]);

  const groupRef = useRef<THREE.Group>(null);
  const timerRef = useRef(new THREE.Timer());

  const _worldMouse = useMemo(() => new THREE.Vector3(), []);
  const _localMouse = useMemo(() => new THREE.Vector3(), []);
  const _invMatrix = useMemo(() => new THREE.Matrix4(), []);
  const _dragPlane = useMemo(
    () => new THREE.Plane(new THREE.Vector3(0, 0, 1), 0),
    [],
  );

  useFrame(({ raycaster, camera }, delta) => {
    if (targets.current.length < 3) return;

    const timer = timerRef.current;
    timer.update();
    const t = timer.getElapsed();
    const dt = Math.min(delta, 0.05);
    const scroll = scrollProgress.get();

    const targetX = Math.sin(scroll * Math.PI * 10) * 4;
    const targetY =
      Math.cos(scroll * Math.PI * 12) * 0.5 + Math.sin(t * 1.2) * 0.15;

    const prev = targets.current[(targetIdx.current + 1) % 3];
    const curr = targets.current[(targetIdx.current + 2) % 3];
    const next = targets.current[targetIdx.current % 3];
    targetIdx.current = (targetIdx.current + 1) % 3;

    raycaster.setFromCamera(ndcMouse.current, camera);
    raycaster.ray.intersectPlane(_dragPlane, _worldMouse);

    if (groupRef.current) {
      groupRef.current.updateMatrixWorld(true);
      _invMatrix.copy(groupRef.current.matrixWorld).invert();
      _localMouse.copy(_worldMouse).applyMatrix4(_invMatrix);
    }
    simMat.uniforms.uMouseLocal.value.copy(_localMouse);

    const localDist = _localMouse.length();
    const targetHover = localDist < 3.5 ? 1.0 : 0.0;
    hoverStrength.current += (targetHover - hoverStrength.current) * 0.08;
    simMat.uniforms.uHoverStrength.value = hoverStrength.current;

    simMat.uniforms.tPrev.value = prev.texture;
    simMat.uniforms.tCurr.value = curr.texture;
    simMat.uniforms.uDeltaT.value = dt;
    simMat.uniforms.uTime.value = t;
    simMat.uniforms.uScrollShape.value = Math.min(scroll * 1.5, 1.0);
    simMat.uniforms.uOffsetX.value = targetX;

    const savedTarget = gl.getRenderTarget();
    gl.setRenderTarget(next);
    gl.render(simScene, simCamera);
    gl.setRenderTarget(savedTarget);

    drawMat.uniforms.tPos.value = next.texture;
    drawMat.uniforms.uTime.value = t;

    // ── Color blending ────────────────────────────────────────────────────
    const palette = isDark ? DARK_PALETTE : LIGHT_PALETTE;

    const colorIndex = scroll * (palette.length - 1);
    const i0 = Math.floor(colorIndex);
    const i1 = Math.min(i0 + 1, palette.length - 1);
    const colorFrac = colorIndex - i0;

    const finalColor = new THREE.Vector3()
      .copy(palette[i0])
      .lerp(palette[i1], colorFrac);

    drawMat.uniforms.uColor1.value.copy(finalColor);
    drawMat.uniforms.uColor2.value.copy(finalColor);

    if (isDark) {
      drawMat.uniforms.uHighlightColor.value.set(1.0, 1.0, 1.0);
      drawMat.uniforms.uAlpha.value = 0.7;
      drawMat.uniforms.uPointSize.value = 1.8;
    } else {
      drawMat.uniforms.uHighlightColor.value.set(0.85, 0.8, 0.95);
      drawMat.uniforms.uAlpha.value = 0.7;
      drawMat.uniforms.uPointSize.value = 1.6;
    }

    if (groupRef.current) {
      groupRef.current.position.x +=
        (targetX - groupRef.current.position.x) * 0.06;
      groupRef.current.position.y +=
        (targetY - groupRef.current.position.y) * 0.06;
      groupRef.current.rotation.y = t * 0.05;
    }
  });

  return (
    <group ref={groupRef}>
      <points geometry={particleGeo} material={drawMat} frustumCulled={false} />
    </group>
  );
}

export default function BackgroundBlob({ isDark }: { isDark: boolean }) {
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, {
    damping: 20,
    stiffness: 100,
    mass: 0.5,
  });
  const [webglFailed, setWebglFailed] = useState(false);

  const ndcMouse = useRef(new THREE.Vector2(0, 0));

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      ndcMouse.current.set(
        (e.clientX / window.innerWidth) * 2 - 1,
        -(e.clientY / window.innerHeight) * 2 + 1,
      );
    };
    const onTouch = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      ndcMouse.current.set(
        (touch.clientX / window.innerWidth) * 2 - 1,
        -(touch.clientY / window.innerHeight) * 2 + 1,
      );
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onTouch, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onTouch);
    };
  }, []);

  useEffect(() => {
    try {
      const canvas = document.createElement("canvas");
      const gl =
        canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      if (!gl) setWebglFailed(true);
    } catch {
      setWebglFailed(true);
    }
  }, []);

  if (webglFailed) {
    return (
      <div
        className={`fixed inset-0 overflow-hidden pointer-events-none z-0 transition-opacity duration-1000 opacity-100`}
        style={{
          background: isDark
            ? "radial-gradient(ellipse 80% 60% at 50% -20%, rgba(120, 80, 200, 0.15), transparent)"
            : "radial-gradient(ellipse 80% 60% at 50% -20%, rgba(120, 80, 200, 0.08), transparent)",
        }}
      />
    );
  }

  return (
    <div
      className={`fixed inset-0 overflow-hidden pointer-events-none z-0 transition-opacity duration-1000 opacity-100`}
    >
      <Canvas
        dpr={[1, 1.5]}
        gl={{
          antialias: false,
          alpha: true,
          powerPreference: "high-performance",
        }}
        camera={{ position: [0, 0, 8], fov: 60 }}
        style={{ background: "transparent" }}
      >
        <group scale={1.8}>
          <ParticleSystem
            isDark={isDark}
            scrollProgress={smoothProgress}
            ndcMouse={ndcMouse}
          />
        </group>
      </Canvas>
    </div>
  );
}